import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Smartphone, X, Download, Share } from "lucide-react";
import { toast } from "sonner";
import { sharePulaNow } from "@/components/SharePula";

const KEY = "pula_install_banner_seen";

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isMobile() {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth < 768;
}
function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export const InstallBanner = () => {
  const [show, setShow] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const { pathname } = useLocation();
  const onChat = ["/chat", "/messages", "/confidante"].some((p) => pathname === p || pathname.startsWith(p + "/"));

  useEffect(() => {
    const onInstalled = () => {
      try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
      setShow(false);
      toast.success("Pula is on your home screen! 🎉 Invite your people?", {
        duration: 8000,
        action: { label: "Share", onClick: () => sharePulaNow() },
      });
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  useEffect(() => {
    let seen = false;
    try { seen = localStorage.getItem(KEY) === "1"; } catch { /* ignore */ }
    if (seen || isStandalone() || !isMobile()) return;

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const t = setTimeout(() => setShow(true), 4000);  // gentle, non-intrusive
    return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
    setShow(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
    }
    dismiss();
  };

  if (!show || onChat) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ y: 90, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 90, opacity: 0 }}
        className="fixed bottom-0 inset-x-0 z-[60] p-3 md:hidden" data-testid="install-banner">
        <div className="pula-card shadow-lg border-pula-green/25 p-3 flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-pula-green/10 grid place-items-center shrink-0">
            <Smartphone className="w-5 h-5 text-pula-green" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-sm text-pula-ink">Add Pula to your home screen</p>
            <p className="text-xs text-pula-inksoft font-body">One tap, no app store — opens fast, uses little space.</p>
          </div>
          {deferred ? (
            <button onClick={install} data-testid="install-banner-install"
              className="inline-flex items-center gap-1 rounded-full bg-pula-green text-white text-sm px-4 py-2 shrink-0">
              <Download className="w-4 h-4" /> Install
            </button>
          ) : (
            <Link to="/get-app" onClick={dismiss} data-testid="install-banner-how"
              className="inline-flex items-center gap-1 rounded-full bg-pula-green text-white text-sm px-4 py-2 shrink-0">
              {isIOS() ? <Share className="w-4 h-4" /> : <Download className="w-4 h-4" />} How
            </Link>
          )}
          <button onClick={dismiss} data-testid="install-banner-dismiss" aria-label="Dismiss" className="text-pula-inksoft hover:text-pula-ink shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
